// Qué le decimos al usuario cuando un turno del agente falla —
// ADR-2026-09-12-el-chat-dice-por-que-fallo-el-turno.
//
// Antes, CUALQUIER falla (red caída, el backend devolviendo 4xx con el motivo del
// proveedor, el watchdog cortando por silencio) terminaba en "No pude conectarme", que
// despistaba: un 400 de Anthropic por una imagen que no pudo bajar no es un problema de
// conexión. Acá se separan los casos y se redacta cada uno. Es un módulo puro.

/** El backend respondió sin stream: status + cuerpo (JSON `{ error, detail }` o texto). */
export class ErrorDelAgente extends Error {
  readonly status: number;
  readonly cuerpo: string;
  constructor(status: number, cuerpo: string) {
    super(`chat-agent error: ${status}`);
    this.name = 'ErrorDelAgente';
    this.status = status;
    this.cuerpo = cuerpo;
  }
}

/**
 * El stream llegó, pero trajo un evento `error` en vez de (o en el medio de) la respuesta:
 * Anthropic lo manda así cuando se satura a mitad de turno, y el backend, cuando Gemini
 * corta sin contenido (ADR-2026-10-12-gemini-vacio-no-es-exito). Antes el chat ignoraba el
 * evento y el turno terminaba en silencio. `para_el_usuario` = el backend ya lo redactó.
 */
export class ErrorEnElStream extends Error {
  readonly tipo: string;
  readonly paraElUsuario: boolean;
  constructor(error: { type?: unknown; message?: unknown; para_el_usuario?: unknown } | null | undefined) {
    super(typeof error?.message === 'string' ? error.message : '');
    this.name = 'ErrorEnElStream';
    this.tipo = typeof error?.type === 'string' ? error.type : '';
    this.paraElUsuario = error?.para_el_usuario === true;
  }
}

export const MENSAJE_SIN_CONEXION = 'No pude conectarme. Probá de nuevo en un toque.';
/** El modelo cerró el turno sin texto ni herramientas: antes la burbuja se borraba y el
 *  usuario no veía nada (ADR-2026-10-12-gemini-vacio-no-es-exito). */
export const MENSAJE_TURNO_VACIO =
  'El modelo no devolvió ninguna respuesta. Probá de nuevo; si sigue pasando, contalo de otra manera o empezá un chat nuevo.';
export const MENSAJE_SIN_RESPUESTA =
  'El agente dejó de responder y corté la espera. Probá de nuevo.';
export const MENSAJE_IMAGEN_NO_DESCARGABLE =
  'El modelo no pudo descargar una de las imágenes del pedido (una foto de producto, un fondo o un adjunto): suele pasar con imágenes muy pesadas. Probá de nuevo; si sigue pasando, sacá esa imagen o mandá una versión más liviana.';
/** Cualquier otro error del proveedor. El motivo crudo NO se muestra: viene en inglés y el
 *  usuario no tiene qué hacer con él (ADR-2026-09-14-el-modelo-ve-el-opt-y-el-error-habla-en-castellano).
 *  Queda en la consola y en `executions.error`. */
export const MENSAJE_ERROR_DEL_MODELO =
  'El modelo devolvió un error y no pude completar el turno. Probá de nuevo; si sigue pasando, avisanos.';
export const MENSAJE_SATURADO = 'El modelo está saturado en este momento. Probá de nuevo en un minuto.';
export const MENSAJE_MUY_LARGO =
  'La conversación se hizo demasiado larga para el modelo. Empezá un chat nuevo o mandá menos adjuntos.';

/** Lo que el backend puso en el cuerpo: `error` (corto, nuestro) y `detail` (del proveedor). */
const leerCuerpo = (cuerpo: string): { error: string; detail: string } => {
  try {
    const j = JSON.parse(cuerpo) as { error?: unknown; detail?: unknown };
    return {
      error: typeof j.error === 'string' ? j.error : '',
      detail: typeof j.detail === 'string' ? j.detail : '',
    };
  } catch {
    return { error: '', detail: cuerpo };
  }
};

/** El `message` del JSON de error del proveedor, si lo hay (Anthropic lo anida). */
const mensajeDelProveedor = (detail: string): string => {
  const m = detail.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/);
  if (m) return m[1].replace(/\\"/g, '"').replace(/\\n/g, ' ');
  return detail.replace(/^\w+ \d{3}:\s*/, '').trim();
};

/** ¿El turno murió porque el contexto no entra? Es el caso que la compactación intenta
 *  evitar y, cuando igual pasa, el que dispara el recorte de emergencia
 *  (ADR-2026-09-13-el-chat-se-compacta-y-se-puede-buscar). */
export function esErrorDeContexto(err: unknown): boolean {
  return mensajeDeErrorDelTurno(err) === MENSAJE_MUY_LARGO;
}

export function mensajeDeErrorDelTurno(err: unknown): string {
  if (err instanceof ErrorEnElStream) {
    if (err.paraElUsuario && err.message) return err.message;
    const todo = `${err.tipo} ${err.message}`.toLowerCase();
    if (todo.includes('overloaded') || todo.includes('rate_limit')) return MENSAJE_SATURADO;
    if (todo.includes('prompt is too long') || todo.includes('context window')) return MENSAJE_MUY_LARGO;
    return MENSAJE_ERROR_DEL_MODELO;
  }
  if (err instanceof ErrorDelAgente) {
    const { error, detail } = leerCuerpo(err.cuerpo);
    const razon = mensajeDelProveedor(detail);
    const todo = `${error} ${razon}`.toLowerCase();
    // "Unable to download the file" y "The request timed out while trying to download the
    // file" son el mismo problema para el usuario: una imagen que el proveedor no pudo bajar.
    if (todo.includes('download')) return MENSAJE_IMAGEN_NO_DESCARGABLE;
    // 503 es Google caído un rato ("The service is currently unavailable"): el backend ya lo
    // repitió una vez, así que lo que sirve es esperar (ADR-2026-10-19-el-chat-reintenta-cuando-gemini-satura).
    if (
      err.status === 429 ||
      err.status === 503 ||
      todo.includes('overloaded') ||
      todo.includes('rate_limit') ||
      todo.includes('rate limit')
    )
      return MENSAJE_SATURADO;
    if (todo.includes('prompt is too long') || todo.includes('too many tokens') || todo.includes('context window'))
      return MENSAJE_MUY_LARGO;
    // Los mensajes propios del backend (plan, permisos) ya están redactados para el usuario.
    if (error && !detail && !error.startsWith('Error de')) return error;
    // Lo demás es del proveedor o de un proxy (un "Bad Gateway", un HTML): nunca crudo.
    if (razon || error) return MENSAJE_ERROR_DEL_MODELO;
    return `No pude completar el turno (error ${err.status}). Probá de nuevo.`;
  }
  if (err instanceof Error && err.name === 'AbortError') return MENSAJE_SIN_RESPUESTA;
  return MENSAJE_SIN_CONEXION;
}
